import { Plus, ShieldAlert } from 'lucide-react';
import { Button } from 'antd';
import { useState } from 'react';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { RecordPromiseModal } from '../../payment-promises/public/RecordPromiseModal';
import { FollowUpModal } from '../components/FollowUpModal';
import { FollowUpsList } from '../components/FollowUpsList';
import type { CollectionFollowUpView } from '../follow-ups.api';
import { useFollowUps } from '../hooks/use-follow-ups';

interface FollowUpsTabProps {
  customerId: string;
  /** `collection.record` shows Record and Correct; the server stays the authority, so this is UX only. */
  canRecord: boolean;
}

/** The Finance tab of a customer's page: every attempt to collect from them, newest first, and the place to log one. */
export function FollowUpsTab({ customerId, canRecord }: FollowUpsTabProps) {
  const followUps = useFollowUps(customerId);
  const [isOpen, setIsOpen] = useState(false);
  const [corrects, setCorrects] = useState<CollectionFollowUpView | null>(null);
  const [promiseFrom, setPromiseFrom] = useState<CollectionFollowUpView | null>(null);

  if (followUps.state.status === 'forbidden') return <Navigate to="/" replace />;
  if (followUps.state.status === 'loading') return <TableSkeleton label="Loading follow-ups" withSearch />;
  if (followUps.state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load the follow-ups" description="Something went wrong loading them. Try reloading the page." />;
  }

  function open(entry: CollectionFollowUpView | null) {
    followUps.resetSaveError();
    setCorrects(entry);
    setIsOpen(true);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-muted">What Finance has done to collect from this customer. An entry is never edited: a correction is a new entry.</p>
        {canRecord && (
          <Button type="primary" icon={<Plus className="size-4" />} onClick={() => open(null)}>
            Record follow-up
          </Button>
        )}
      </div>
      <FollowUpsList
        items={followUps.state.page.items}
        total={followUps.state.page.total}
        list={followUps.list}
        isRefreshing={followUps.isRefreshing}
        onCorrect={canRecord ? open : undefined}
        onRecordPromise={canRecord ? setPromiseFrom : undefined}
      />
      <FollowUpModal
        open={isOpen}
        corrects={corrects}
        error={followUps.saveError}
        submitting={followUps.saving}
        onSubmit={followUps.record}
        onClose={() => setIsOpen(false)}
      />
      <RecordPromiseModal open={promiseFrom !== null} customerId={customerId} followUpId={promiseFrom?.id} onClose={() => setPromiseFrom(null)} />
    </div>
  );
}
